/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV6RealGifts) return;

  const MODE = 'personalized_real_gifts';
  const GROUP = '_safewalk_package_group';
  const ROLE = '_safewalk_package_role';
  const REAL = '_safewalk_real_gift';
  const KACHING = '__kaching_bundles';
  const DOG_FALLBACK = 'שם הכלב';
  const PHONE_FALLBACK = 'מספר טלפון';
  const ROLES = ['gift_tags', 'gift_dispenser', 'gift_roll'];
  const copy = () => window.SafeWalkV6RealGiftCopy || {};
  const dogProperty = () => copy().dogNameProperty || DOG_FALLBACK;
  const phoneProperty = () => copy().phoneProperty || PHONE_FALLBACK;
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const productId = (item) => Number(item?.product_id || 0);
  const parentKey = (item) => String(item?.parent_relationship?.parent_key || '');
  const metadataFor = (item) => {
    const raw = propertiesFor(item)[KACHING];
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };
  const samePropertyValue = (left, right) => {
    if (typeof left === 'string' || typeof right === 'string') return left === right;
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch (error) {
      return false;
    }
  };
  const isMarkedGift = (item) => {
    const properties = propertiesFor(item);
    return String(properties[REAL] || '').toLowerCase() === 'true'
      && Boolean(String(properties[GROUP] || '').trim())
      && ROLES.includes(properties[ROLE]);
  };
  const commerceContract = () => window.SafeWalkV6RealGiftContract;
  const validationKey = (personalization) => {
    const validator = commerceContract()?.validationKey;
    return typeof validator === 'function' ? validator(personalization) : 'validation';
  };
  const validateConfig = (config) => commerceContract()?.validateConfig(config) === true;
  const splitPersonalization = (mode, properties = {}) => {
    const lineProperties = { ...properties };
    const personalization = {
      dogName: String(lineProperties[dogProperty()] || '').trim(),
      phone: String(lineProperties[phoneProperty()] || '').trim()
    };
    if (mode === MODE) {
      delete lineProperties[dogProperty()];
      delete lineProperties[phoneProperty()];
    }
    return { lineProperties, personalization };
  };
  const mergeGiftProperties = (existing, role, groupId, contract = {}) => ({
    ...existing,
    ...contract,
    [REAL]: 'true',
    [GROUP]: String(groupId),
    [ROLE]: role
  });
  const mergeTagProperties = (existing = {}, personalization = {}, { groupId, contract } = {}) => {
    const merged = mergeGiftProperties(existing, 'gift_tags', groupId, contract);
    delete merged[DOG_FALLBACK];
    delete merged[PHONE_FALLBACK];
    delete merged[dogProperty()];
    delete merged[phoneProperty()];
    const dogName = String(personalization.dogName || '').trim();
    const phone = String(personalization.phone || '').trim();
    if (dogName) merged[dogProperty()] = dogName;
    if (phone) merged[phoneProperty()] = phone;
    return merged;
  };
  const classifyCart = (cart, config) => commerceContract()?.classifyCart(cart, config)
    || { groups: [], ordinaryItems: cart?.items || [], invalidGiftItems: [] };
  const readRootConfig = (root) => commerceContract()?.readRootConfig(root)
    || { identities: [], realGiftIds: {} };
  const configFromRoot = (root) => commerceContract()?.configFromRoot(root)
    || { identities: [], realGiftIds: {}, giftIds: {} };
  const mainContractProperties = (config, groupId) => commerceContract()
    ?.mainContractProperties(config, groupId) || {};
  const identityFor = (config, role) => (config.identities || [])
    .find((identity) => identity.role === role);
  const itemMatchesIdentity = (item, identity) => Boolean(identity)
    && productId(item) === Number(identity.productId)
    && variantId(item) === Number(identity.variantId);
  const personalizationControlKey = (input) => input?.dataset.safewalkPersonalizationKey
    || input?.name?.slice(11, -1)
    || '';
  const readPersonalization = (controls = []) => {
    const result = {};
    controls.forEach((input) => {
      const key = input.dataset.safewalkPersonalizationProperty || input.name?.slice(11, -1) || '';
      if (key && input.value?.trim()) result[key] = input.value.trim();
    });
    return result;
  };
  const fetchCart = async () => {
    const response = await window.fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`cart-${response.status}`);
    return response.json();
  };
  const sectionIds = () => {
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    document.querySelectorAll(
      '[data-safewalk-real-gifts-section][data-section-id], cart-items[data-id], #main-cart-footer[data-id]'
    )
      .forEach((node) => ids.add(node.dataset.sectionId || node.dataset.id));
    document.querySelector('cart-drawer')?.getSectionsToRender?.()
      .forEach((section) => ids.add(section.id));
    return [...ids].filter(Boolean).slice(0, 5);
  };
  const changeLine = async (item, properties, renderSections = false) => {
    const payload = { id: item.key, quantity: Number(item.quantity || 1), properties };
    if (renderSections) {
      payload.sections = sectionIds();
      payload.sections_url = window.location.pathname;
    }
    const response = await window.fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || !body) throw new Error(`change-${response.status}`);
    return body;
  };
  const replaceSection = (sectionId, html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const current = document.getElementById(`shopify-section-${sectionId}`) || document.getElementById(sectionId);
    const fresh = parsed.getElementById(`shopify-section-${sectionId}`) || parsed.getElementById(sectionId);
    if (current && fresh) current.innerHTML = fresh.innerHTML;
  };
  const render = (body) => {
    if (!body?.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents && body.sections['cart-drawer']) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(body);
    }
    Object.entries(body.sections).forEach(([id, html]) => {
      if (id !== 'cart-drawer' && id !== 'cart-icon-bubble') replaceSection(id, html);
    });
    document.dispatchEvent(new CustomEvent('safewalk:real-gifts-rendered'));
  };
  const expectedGift = (cart, main, config, role) => {
    const identity = identityFor(config, role);
    const privateId = String(metadataFor(main)?.id || '');
    return (cart.items || []).find((item) => {
      const metadata = metadataFor(item);
      return item !== main
        && itemMatchesIdentity(item, identity)
        && lineTotal(item) === 0
        && Number(item.quantity) === 1
        && parentKey(item) === String(main?.key || '')
        && metadata?.deal === config.deal
        && metadata?.gift === config.giftIds[role]
        && String(metadata?.id || '') === privateId;
    });
  };
  const rollbackGroup = async (groupId) => {
    try {
      const cart = await fetchCart();
      const main = cart.items.find((item) => {
        const properties = propertiesFor(item);
        return properties[ROLE] === 'main' && String(properties[GROUP] || '') === String(groupId);
      });
      const privateId = String(metadataFor(main)?.id || '');
      const linked = cart.items.filter((item) => {
        const properties = propertiesFor(item);
        return String(properties[GROUP] || '') === String(groupId)
          || (privateId && String(metadataFor(item)?.id || '') === privateId);
      });
      const updates = Object.fromEntries(linked.filter((item) => item.key).map((item) => [item.key, 0]));
      if (!Object.keys(updates).length) return true;
      const response = await window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: sectionIds(),
          sections_url: window.location.pathname
        })
      });
      const body = await response.json().catch(() => null);
      if (!response.ok || !body) return false;
      const remaining = (await fetchCart()).items.some((item) => {
        const properties = propertiesFor(item);
        return String(properties[GROUP] || '') === String(groupId)
          || (privateId && String(metadataFor(item)?.id || '') === privateId);
      });
      if (remaining) return false;
      render(body);
      return true;
    } catch (error) {
      return false;
    }
  };
  const reconcileGroup = async ({ root, groupId, personalization = {} }) => {
    const config = configFromRoot(root);
    if (!groupId || !validateConfig(config) || Object.values(config.giftIds).some((id) => !id)) {
      return { ok: false, reason: 'gift-config' };
    }
    const personalizationError = validationKey(personalization);
    if (personalizationError) {
      const rolledBack = await rollbackGroup(groupId);
      return { ok: false, rolledBack, reason: `gift-${personalizationError}` };
    }
    try {
      let lastBody = null;
      for (const role of ROLES) {
        const cart = await fetchCart();
        const main = cart.items.find((item) => {
          const properties = propertiesFor(item);
          return properties[ROLE] === 'main' && String(properties[GROUP] || '') === String(groupId);
        });
        if (!main) throw new Error('gift-main');
        const gift = expectedGift(cart, main, config, role);
        if (!gift) throw new Error(`gift-${role}`);
        const contract = commerceContract()?.giftContractProperties(config, role, metadataFor(main)?.id) || {};
        const properties = role === 'gift_tags'
          ? mergeTagProperties(propertiesFor(gift), personalization, { groupId, contract })
          : mergeGiftProperties(propertiesFor(gift), role, groupId, contract);
        lastBody = await changeLine(gift, properties, role === 'gift_roll');
      }
      const verified = classifyCart(await fetchCart(), config)
        .groups.find((group) => group.groupId === String(groupId) && group.complete);
      if (!verified) throw new Error('gift-verification');
      render(lastBody);
      return { ok: true, group: verified };
    } catch (error) {
      const rolledBack = await rollbackGroup(groupId);
      return {
        ok: false,
        rolledBack,
        reason: error instanceof Error ? error.message : 'gift-error'
      };
    }
  };
  const updateTag = async ({ groupId, personalization }) => {
    const personalizationError = validationKey(personalization);
    if (personalizationError) throw new Error(`tag-${personalizationError}`);
    const config = commerceContract()?.configFromRegistry?.();
    let cart = await fetchCart();
    const tag = cart.items.find((item) => {
      const properties = propertiesFor(item);
      return isMarkedGift(item)
        && properties[ROLE] === 'gift_tags'
        && String(properties[GROUP] || '') === String(groupId);
    });
    if (!tag || lineTotal(tag) !== 0 || !validateConfig(config)) throw new Error('tag-line');
    const previousProperties = { ...propertiesFor(tag) };
    try {
      const body = await changeLine(tag, mergeTagProperties(previousProperties, personalization, { groupId }), true);
      cart = await fetchCart();
      const saved = cart.items.find((item) => {
        const properties = propertiesFor(item);
        return properties[ROLE] === 'gift_tags' && String(properties[GROUP] || '') === String(groupId);
      });
      if (!saved
        || lineTotal(saved) !== 0
        || !samePropertyValue(propertiesFor(saved)[KACHING], previousProperties[KACHING])
        || !classifyCart(cart, config).groups
          .some((group) => group.groupId === String(groupId) && group.complete)) {
        throw new Error('tag-verification');
      }
      render(body);
      return saved;
    } catch (error) {
      try {
        const currentCart = await fetchCart();
        const changedTag = currentCart.items.find((item) => {
          const properties = propertiesFor(item);
          return properties[ROLE] === 'gift_tags' && String(properties[GROUP] || '') === String(groupId);
        });
        if (changedTag && lineTotal(changedTag) === 0) {
          await changeLine(changedTag, previousProperties, true);
        }
      } catch (restoreError) {
        // Checkout remains fail-closed until the cart returns to a valid contract.
      }
      throw error;
    }
  };
  const cleanupStaleGifts = () => window.SafeWalkV6RealGiftLifecycle
    ?.reconcileExistingCart?.() || Promise.resolve(false);
  window.SafeWalkV6RealGifts = Object.freeze({
    validationKey,
    validateConfig,
    readRootConfig,
    configFromRoot,
    mainContractProperties,
    personalizationControlKey,
    readPersonalization,
    splitPersonalization,
    mergeTagProperties,
    classifyCart,
    reconcileGroup,
    rollbackGroup,
    updateTag,
    cleanupStaleGifts,
    render
  });
})();
